// course.selector.ts
import { SystemProcessLoadingStatus } from './../../../../../models/global-classes';
import { createFeatureSelector, createSelector } from '@ngrx/store';
import { CourseGenerationTracker, CourseState } from './course.reducer';
import {
  CourseDetailsModelBase,
  CourseHeirarchyModel,
  CourseHierarchyItem,
  CourseHierarchyItemTypeEnum,
  CourseItemStatus,
  CourseSettingsModel,
  StudentItem,
} from 'src/app/components/user-dashboard/product/models/course';
import { CourseCompoundState } from '../../../learmo.reducer';

// Create a feature selector for the 'products' feature
export const selectCompoundCourseState =
  createFeatureSelector<CourseCompoundState>('course');
export const selectCourseState = createSelector(
  selectCompoundCourseState,
  (state) => state.main,
);

// Create a selector to get the products from the state (maintaining the same name)

export const selectCurrentCourseHeirarchy = createSelector(
  selectCourseState,
  (state): CourseHeirarchyModel => {
    return state.currentCourseHeirarchy;
  },
);

export const selectCurrentCourseSettings = createSelector(
  selectCourseState,
  (state): CourseSettingsModel => {
    return state.currentCourseHeirarchy.settings;
  },
);

// Gets the entire progress map
export const selectAllGenerationProgress = createSelector(
  selectCourseState,
  (state) => state.generationProgress,
);

// Gets the specific tracker for a single course
export const selectCourseGenerationProgress = (courseId: string | number) =>
  createSelector(
    selectCourseState,
    (state): CourseGenerationTracker | null =>
      state.generationProgress[courseId] || null,
  );

export const selectCurrentCourseItemsMappingArray = createSelector(
  selectCourseState,
  (state): number[] => {
    let ItemsIdsMap = [];
    state.currentCourseHeirarchy.hierarchy.items.forEach((level1) => {
      level1.items.forEach((level2) => {
        if (
          level2.type == CourseHierarchyItemTypeEnum.subModule &&
          !!level2.items?.length
        ) {
          level2.items.forEach((level3) => {
            ItemsIdsMap.push(level3.id);
          });
        } else {
          ItemsIdsMap.push(level2.id);
        }
      });
    });

    return ItemsIdsMap;
  },
);

export const isCurrentCourseHeirarchyLoaded = createSelector(
  selectCourseState,
  (state) =>
    state.currentCourseHeirarchyLoadProcess.status == 'loading' ? false : true,
);
export const isCurrentCourseHeirarchyProcessing = createSelector(
  selectCourseState,
  (state) => ({
    status: state.currentCourseHeirarchyLoadProcess.status,
    loadingMessage: state.currentCourseHeirarchyLoadProcess.loadingMessage,
    error: state.currentCourseHeirarchyLoadProcess.error,
  }),
);
// export const isCurrentCourseLoadingError = createSelector(selectCourseState, (state) => state.currentCourseHeirarchyProcess.error);

export const selectCourseItem = (
  id: number,
  type: CourseHierarchyItemTypeEnum,
) =>
  createSelector(selectCourseState, (state) => {
    return findCourseItem(
      state.currentCourseHeirarchy.hierarchy.items,
      id,
      type,
    );
  });

export const isOnlyPublishedItemInCourse = (
  id: number,
  type: CourseHierarchyItemTypeEnum,
) =>
  createSelector(selectCourseState, (state): boolean => {
    const allItems = flattenCourseItems(
      state.currentCourseHeirarchy.hierarchy.items,
    );

    const isModule = type === CourseHierarchyItemTypeEnum.module;

    const filteredItems = allItems.filter((item) => {
      const isPublished = item.status === CourseItemStatus.PUBLISHED;

      if (isModule) {
        return isPublished && item.type === CourseHierarchyItemTypeEnum.module;
      }

      return isPublished && item.type !== CourseHierarchyItemTypeEnum.module;
    });

    const matches = filteredItems.filter(
      (item) => item.id === id && item.type === type,
    );
    return filteredItems.length != 0 && matches.length === filteredItems.length;
  });

export const selectQuizQuestions = (id: number) =>
  createSelector(selectCourseState, (state) => {
    return findCourseItem(
      state.currentCourseHeirarchy.hierarchy.items,
      id,
      CourseHierarchyItemTypeEnum.quiz,
    )?.questions;
  });

export const selectItemAdditionalContext = (
  id: number,
  type: CourseHierarchyItemTypeEnum,
) =>
  createSelector(selectCourseState, (state) => {
    return getItemAdditionalContext(
      state.currentCourseHeirarchy.hierarchy.items,
      id,
      type,
    );
  });

export const selectItemDependentContext = (
  id: number,
  type: CourseHierarchyItemTypeEnum,
  parentId?: number,
) =>
  createSelector(selectCourseState, (state) => {
    return getItemDependentContext(
      state.currentCourseHeirarchy.hierarchy.items,
      id,
      type,
      null,
      parentId,
    );
  });

// Separate Functions
const findCourseItem = (items, id, type) => {
  for (let item of items) {
    if (item.id === id && item.type === type) {
      return item;
    }
    if (item.items) {
      const foundItem = findCourseItem(item.items, id, type);
      if (foundItem) {
        return foundItem;
      }
    }
  }
  return null;
};

const getItemDependentContext = (
  items: CourseHierarchyItem[],
  id: number,
  type: string,
  parentName?: string,
  parentId?: number,
) => {
  const contextItems: CourseHierarchyItem[] = [];
  const defaultContextItems: CourseHierarchyItem[] = [];
  let catchedQuiz = false;
  for (const item of items) {
    const newItem: CourseHierarchyItem = { ...item, parentName };
    if (newItem.type != CourseHierarchyItemTypeEnum.lesson) {
      newItem.disabled = true;
    }

    if (item.id === id && item.type === type && !catchedQuiz) {
      catchedQuiz = true;
    }

    if (
      item.type == CourseHierarchyItemTypeEnum.lesson &&
      parentId === item.parentId &&
      !catchedQuiz
    ) {
      defaultContextItems.push(newItem);
    }

    if (parentName) {
      contextItems.push(newItem);
    }

    if (item.items?.length) {
      const {
        contextItems: childContextItems,
        defaultContextItems: childDefaultItems,
      } = getItemDependentContext(item.items, id, type, item.name, parentId);

      contextItems.push(...childContextItems);
      defaultContextItems.push(...childDefaultItems);
    }
  }

  return { contextItems, defaultContextItems };
};

const getItemAdditionalContext = (items, id, type) => {
  function removeHtmlTags(input) {
    return input.replace(/<[^>]*>/g, ' ');
  }

  let contextMap = '';

  const getItemAdditionalSubContext = (items, id, type) => {
    for (let item of items) {
      if (item.type == 'module') {
        contextMap = item.name;
      } else {
        switch (item.type) {
          case CourseHierarchyItemTypeEnum.quiz:
            let questionsContext = (item.questions || []).map((question) => {
              return `
                ------------------------
                Already used questions: /${question.prompt} } \n
                ------------------------
                `;
            });
            contextMap += `/${questionsContext} \n`;
            break;

          default:
            contextMap += `/${item.name} (${item.type}) ${
              item?.content ? item.content : ''
            } \n`;
            break;
        }
      }
      contextMap += item.name;
      if (item.id === id && item.type === type) {
        return removeHtmlTags(contextMap);
      }
      if (item.items) {
        const foundItem = getItemAdditionalSubContext(item.items, id, type);
        if (foundItem) {
          return foundItem;
        }
      }
      contextMap = contextMap.slice(0, -item.name.length); // Remove the item name if not found in this path
    }
    return null;
  };

  return getItemAdditionalSubContext(items, id, type);
};

const flattenCourseItems = (
  items: CourseHierarchyItem[],
): CourseHierarchyItem[] => {
  const result: CourseHierarchyItem[] = [];

  const walk = (list: CourseHierarchyItem[]) => {
    for (const item of list) {
      result.push(item);
      if (item.items?.length) {
        walk(item.items);
      }
    }
  };

  walk(items);
  return result;
};
